import { useState } from "react";
import {
    BiLock,
    BiLockOpen,
    BiShow,
    BiHide,
    BiArrowBack,
    BiCheckCircle,
    BiErrorCircle,
} from "react-icons/bi";
import { FaKey, FaShieldAlt, FaCheck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { updateClientPassword } from "../services/ClientProfileService";
import { useSelector } from "react-redux";
import { showToast } from "toast/toastHelper";
import { useTranslation } from "react-i18next";

const ClientChangePassword = () => {
    const { t } = useTranslation(["client"]);

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const accessToken = useSelector((state) => state.auth.accessToken);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = t("changePassword.errors.currentRequired");
        } else if (formData.currentPassword.length < 6) {
            newErrors.currentPassword = t("changePassword.errors.currentMinLength");
        }

        if (!formData.newPassword) {
            newErrors.newPassword = t("changePassword.errors.newRequired");
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = t("changePassword.errors.newMinLength");
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(formData.newPassword)) {
            newErrors.newPassword = t("changePassword.errors.newComplexity");
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = t("changePassword.errors.confirmRequired");
        } else if (formData.confirmPassword !== formData.newPassword) {
            newErrors.confirmPassword = t("changePassword.errors.confirmMismatch");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (!validateForm()) {
            setLoading(false);
            return;
        }
        try {
            const res = await updateClientPassword({
                accessToken: accessToken,
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
                confirmPassword: formData.confirmPassword,
            });

            if (res?.ok) {
                showToast(t("changePassword.successToast"), 'success');
                setSuccess(true);
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { level: 0, textKey: 'changePassword.strengthWeak', color: 'bg-gray-200', width: '0%' };
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 10) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^a-zA-Z0-9]/.test(password)) strength++;

        const levels = [
            { textKey: 'changePassword.strengthWeak', color: 'bg-red-500', width: '20%' },
            { textKey: 'changePassword.strengthWeak', color: 'bg-orange-500', width: '40%' },
            { textKey: 'changePassword.strengthMedium', color: 'bg-yellow-500', width: '60%' },
            { textKey: 'changePassword.strengthStrong', color: 'bg-green-500', width: '80%' },
            { textKey: 'changePassword.strengthVeryStrong', color: 'bg-emerald-500', width: '100%' }
        ];

        const index = Math.min(Math.floor(strength / 2), 4);
        return { ...levels[index], level: index };
    };

    const passwordStrength = getPasswordStrength(formData.newPassword);

    const requirements = [
        { label: t("changePassword.requirements.minLength"), met: formData.newPassword.length >= 6 },
        { label: t("changePassword.requirements.uppercase"), met: /[A-Z]/.test(formData.newPassword) },
        { label: t("changePassword.requirements.lowercase"), met: /[a-z]/.test(formData.newPassword) },
        { label: t("changePassword.requirements.number"), met: /[0-9]/.test(formData.newPassword) },
    ];

    return (
        <div className="min-h-screen w-full flex items-center justify-center px-3 sm:px-4 py-6 sm:py-10 bg-gradient-to-br from-[#eaf6f4] via-white to-[#eaf6f4] dark:!from-[#121818] dark:!via-[#18181b] dark:!to-[#121818] transition-colors duration-300">
            <div className="w-full max-w-md">

                {/* Form Card */}
                <form
                    onSubmit={handleSubmit}
                    className="bg-white dark:!bg-gray-900 rounded-3xl shadow-xl dark:!shadow-black/40 border border-gray-100 dark:!border-gray-800 overflow-hidden"
                >

                    {/* Top accent bar */}
                    <div className="h-1.5 w-full bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600"></div>

                    {/* Header inside card */}
                    <div className="px-5 sm:px-7 pt-6 pb-4 flex items-center gap-3 border-b border-gray-100 dark:!border-gray-800">
                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 dark:!from-teal-600 dark:!to-teal-800 flex items-center justify-center shadow-md shadow-teal-200/60 dark:!shadow-black/40 flex-shrink-0">
                            <FaKey className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:!text-gray-100">
                                {t("changePassword.title")}
                            </h2>
                            <p className="text-[11px] sm:text-xs text-gray-500 dark:!text-gray-400 mt-0.5">
                                {t("changePassword.subtitle")}
                            </p>
                        </div>
                    </div>

                    {/* Success Message */}
                    {success && (
                        <div className="mx-5 sm:mx-7 mt-5 p-3.5 bg-green-50 dark:!bg-green-950/40 border border-green-200 dark:!border-green-800/70 rounded-2xl flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                <BiCheckCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-green-700 dark:!text-green-300 text-sm font-bold">
                                    {t("changePassword.successTitle")}
                                </p>
                                <p className="text-green-600 dark:!text-green-400 text-[11px] mt-0.5">
                                    {t("changePassword.successSubtitle")}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="p-5 sm:p-7 space-y-5">
                        {/* Current Password */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 dark:!text-gray-400 mb-2">
                                {t("changePassword.currentPassword")}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="currentPassword"
                                    value={formData.currentPassword}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3.5 pl-12 border-2 rounded-xl focus:ring-2 outline-none transition-all duration-300 text-gray-700 dark:!text-gray-100 font-medium bg-gray-50 dark:!bg-gray-800 placeholder:text-gray-400 dark:!placeholder:text-gray-500 text-sm ${errors.currentPassword
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-200 dark:!focus:ring-red-900/60'
                                            : 'border-gray-200 dark:!border-gray-700 focus:border-teal-500 dark:!focus:border-teal-500 focus:ring-teal-200 dark:!focus:ring-teal-900/60 focus:bg-white dark:!focus:bg-gray-800'
                                        }`}
                                    placeholder={t("changePassword.currentPasswordPlaceholder")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:!text-gray-500 hover:text-teal-600 dark:!hover:text-teal-400 transition-colors"
                                >
                                    {showPassword ? <BiHide className="w-5 h-5" /> : <BiShow className="w-5 h-5" />}
                                </button>
                            </div>
                            {errors.currentPassword && (
                                <p className="text-red-500 dark:!text-red-400 text-xs mt-2 mr-2 flex items-center gap-1">
                                    <BiErrorCircle className="w-3.5 h-3.5" />
                                    {errors.currentPassword}
                                </p>
                            )}
                        </div>

                        <div className="h-px bg-gray-100 dark:!bg-gray-800"></div>

                        {/* New Password */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 dark:!text-gray-400 mb-2">
                                {t("changePassword.newPassword")}
                            </label>
                            <div className="relative">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3.5 pl-12 border-2 rounded-xl focus:ring-2 outline-none transition-all duration-300 text-gray-700 dark:!text-gray-100 font-medium bg-gray-50 dark:!bg-gray-800 placeholder:text-gray-400 dark:!placeholder:text-gray-500 text-sm ${errors.newPassword
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-200 dark:!focus:ring-red-900/60'
                                            : 'border-gray-200 dark:!border-gray-700 focus:border-teal-500 dark:!focus:border-teal-500 focus:ring-teal-200 dark:!focus:ring-teal-900/60 focus:bg-white dark:!focus:bg-gray-800'
                                        }`}
                                    placeholder={t("changePassword.newPasswordPlaceholder")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:!text-gray-500 hover:text-teal-600 dark:!hover:text-teal-400 transition-colors"
                                >
                                    {showNewPassword ? <BiHide className="w-5 h-5" /> : <BiShow className="w-5 h-5" />}
                                </button>
                            </div>
                            {errors.newPassword && (
                                <p className="text-red-500 dark:!text-red-400 text-xs mt-2 mr-2 flex items-center gap-1">
                                    <BiErrorCircle className="w-3.5 h-3.5" />
                                    {errors.newPassword}
                                </p>
                            )}

                            {/* Password Strength Indicator */}
                            {formData.newPassword && (
                                <div className="mt-4 p-4 bg-gray-50 dark:!bg-gray-800/60 rounded-2xl border border-gray-100 dark:!border-gray-800">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-gray-500 dark:!text-gray-400">
                                            {t("changePassword.strengthLabel")}
                                        </span>
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${passwordStrength.level <= 1
                                                ? 'text-red-600 dark:!text-red-400 bg-red-100 dark:!bg-red-950/60'
                                                : passwordStrength.level === 2
                                                    ? 'text-yellow-600 dark:!text-yellow-400 bg-yellow-100 dark:!bg-yellow-950/60'
                                                    : passwordStrength.level === 3
                                                        ? 'text-green-600 dark:!text-green-400 bg-green-100 dark:!bg-green-950/60'
                                                        : 'text-emerald-600 dark:!text-emerald-400 bg-emerald-100 dark:!bg-emerald-950/60'
                                            }`}>
                                            {t(passwordStrength.textKey)}
                                        </span>
                                    </div>

                                    <div className="w-full h-2 bg-gray-200 dark:!bg-gray-700 rounded-full overflow-hidden mb-3">
                                        <div
                                            className={`h-full ${passwordStrength.color} transition-all duration-500 rounded-full`}
                                            style={{ width: passwordStrength.width }}
                                        ></div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        {requirements.map((req, idx) => (
                                            <div
                                                key={idx}
                                                className={`flex items-center gap-1.5 text-[11px] font-medium transition-colors ${req.met
                                                        ? 'text-emerald-600 dark:!text-emerald-400'
                                                        : 'text-gray-400 dark:!text-gray-500'
                                                    }`}
                                            >
                                                <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${req.met
                                                        ? 'bg-emerald-500 dark:!bg-emerald-600'
                                                        : 'bg-gray-200 dark:!bg-gray-700'
                                                    }`}>
                                                    <FaCheck className={`w-2 h-2 ${req.met ? 'text-white' : 'text-gray-400 dark:!text-gray-500'
                                                        }`} />
                                                </span>
                                                {req.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 dark:!text-gray-400 mb-2">
                                {t("changePassword.confirmPassword")}
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3.5 pl-12 border-2 rounded-xl focus:ring-2 outline-none transition-all duration-300 text-gray-700 dark:!text-gray-100 font-medium bg-gray-50 dark:!bg-gray-800 placeholder:text-gray-400 dark:!placeholder:text-gray-500 text-sm ${errors.confirmPassword
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-200 dark:!focus:ring-red-900/60'
                                            : formData.confirmPassword && formData.confirmPassword === formData.newPassword
                                                ? 'border-emerald-400 dark:!border-emerald-600 focus:border-emerald-500 focus:ring-emerald-200 dark:!focus:ring-emerald-900/60'
                                                : 'border-gray-200 dark:!border-gray-700 focus:border-teal-500 dark:!focus:border-teal-500 focus:ring-teal-200 dark:!focus:ring-teal-900/60 focus:bg-white dark:!focus:bg-gray-800'
                                        }`}
                                    placeholder={t("changePassword.confirmPasswordPlaceholder")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:!text-gray-500 hover:text-teal-600 dark:!hover:text-teal-400 transition-colors"
                                >
                                    {showConfirmPassword ? <BiHide className="w-5 h-5" /> : <BiShow className="w-5 h-5" />}
                                </button>
                                {formData.confirmPassword && formData.confirmPassword === formData.newPassword && (
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                        <FaCheck className="w-2.5 h-2.5 text-white" />
                                    </div>
                                )}
                            </div>
                            {errors.confirmPassword && (
                                <p className="text-red-500 dark:!text-red-400 text-xs mt-2 mr-2 flex items-center gap-1">
                                    <BiErrorCircle className="w-3.5 h-3.5" />
                                    {errors.confirmPassword}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="px-5 sm:px-7 pb-5 sm:pb-7">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full group flex items-center justify-center gap-3 bg-gradient-to-r from-teal-600 to-teal-700 dark:!from-teal-700 dark:!to-teal-800 text-white px-6 py-4 rounded-2xl font-bold shadow-lg shadow-teal-200/60 dark:!shadow-black/40 hover:shadow-xl hover:shadow-teal-300/60 dark:!hover:shadow-black/60 transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>{t("changePassword.submitting")}</span>
                                </>
                            ) : (
                                <>
                                    <BiLockOpen className="w-5 h-5 transition-transform group-hover:rotate-12" />
                                    <span>{t("changePassword.submit")}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Security Tip */}
                <div className="mt-4 p-4 bg-teal-50/60 dark:!bg-teal-950/30 border border-teal-100 dark:!border-teal-900/50 rounded-2xl flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 dark:!bg-teal-900/60 flex items-center justify-center flex-shrink-0">
                        <FaShieldAlt className="w-4 h-4 text-teal-600 dark:!text-teal-400" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-teal-800 dark:!text-teal-300 mb-0.5">
                            {t("changePassword.securityTipTitle")}
                        </p>
                        <p className="text-[11px] text-teal-700 dark:!text-teal-400 leading-relaxed">
                            {t("changePassword.securityTipText")}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ClientChangePassword;